'use client';

import type { ReactNode } from 'react';

import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { fillTemplate } from '@eliadmin/core/src/utils/nt/text';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { useShippingDates } from './shipping';
import { stockState, useActiveVariant } from './use-active-variant';

/**
 * Delivery box item whose title holds `{datum}` („Doručenie už {datum}“): the date is the working day
 * after shipping, computed after mount (the title keeps its line hidden until then). The promise
 * holds only for goods in stock, so the item hides for a variant without stock.
 */
export function DeliveryDateItem({
  title,
  cutoff,
  weekdays,
  icon,
  subtitle,
  className,
}: {
  /** Item name with `{datum}`. */
  title: string;
  /** „Produkt - uzávierka“ („14:00“). */
  cutoff: string;
  /** „Produkt - dni“. */
  weekdays: string;
  icon: ReactNode;
  subtitle: ReactNode;
  className?: string;
}) {
  const locale = useLocaleStore(state => state.currentLocale);
  const product = useVariantsStore(state => state.product);
  const variant = useActiveVariant();
  const dates = useShippingDates(cutoff, weekdays, locale);
  if (!stockState(variant ?? product).inStock) return null;

  return (
    <li className={className}>
      {icon}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className={cn('nt-body-l-bold text-[#0D110D]', !dates && 'invisible')}>
          {fillTemplate(title, { datum: dates?.deliveryDate ?? '' })}
        </p>
        {subtitle}
      </div>
    </li>
  );
}
